import { useState, useEffect } from "react";
import "./App.css";
import { getWeather } from "./weather";
import { fishing_forecast } from "./FishFormula";
import { FISH_SPECIES } from "./lib/fishSpecies";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFishFins, faMapLocation } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { Analytics } from "@vercel/analytics/react";

function ForecastGauge({ percentage }) {
  const size = 160;
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - percentage / 100);

  return (
    <div className="gauge">
      <svg viewBox={`0 0 ${size} ${size}`}>
        <circle
          className="gauge-track"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <motion.circle
          className="gauge-progress"
          cx={size / 2}
          cy={size / 2}
          r={radius}
          strokeWidth={strokeWidth}
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset: offset }}
          transition={{ duration: 1.1, ease: "easeOut", delay: 0.15 }}
        />
      </svg>
      <div className="gauge-label">
        <span className="gauge-value">{Math.round(percentage)}%</span>
        <span className="gauge-caption">Chance of a Bite</span>
      </div>
    </div>
  );
}

function App() {
  const [location, setLocation] = useState(null);
  const [coords, setCoords] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [forecastError, setForecastError] = useState(null);
  const [hasSelected, setHasSelected] = useState(false);

  useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;
        setCoords({ lat, lon });

        getWeather(lat, lon).then((weather) => {
          if (weather) {
            setLocation(weather.location);
          }
        });
      },
      () => {
        setLocationError(
          "Location unavailable — enable location access to see a forecast.",
        );
      },
    );
  }, []);

  const handleSelectChange = (event) => {
    if (!coords) return;

    setHasSelected(true);
    setForecast(null);
    setForecastError(null);
    fishing_forecast(event.target.value, coords)
      .then((percentage) => setForecast(percentage))
      .catch(() =>
        setForecastError("Couldn't fetch the forecast — please try again."),
      );
  };

  return (
    <div className="container">
      <header className="header">
        <p className="eyebrow">Live Conditions Report</p>
        <h1 className="headerText">
          <FontAwesomeIcon icon={faFishFins} /> Fishing Forecast
        </h1>
      </header>

      <div className="dropDownContainer">
        <p className="dropDownText">Select Fish Type:</p>
        <select
          onChange={handleSelectChange}
          className={"dropDown"}
          disabled={!coords}
        >
          <option hidden disabled selected value>
            Choose a species…
          </option>
          {FISH_SPECIES.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      <div className="locationContainer">
        {location ? (
          <p className="locationText">
            <FontAwesomeIcon icon={faMapLocation} /> Location: {location}
          </p>
        ) : (
          <p className={`locationText${locationError ? " error-text" : ""}`}>
            {locationError || "No location available"}
          </p>
        )}
      </div>

      {forecastError ? (
        <p className="forecast-error">{forecastError}</p>
      ) : forecast !== null ? (
        <motion.div
          className="fish-chance-box"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            type: "spring",
            stiffness: 100,
            damping: 10,
            duration: 2,
          }}
        >
          <ForecastGauge percentage={forecast} />
        </motion.div>
      ) : hasSelected ? (
        <div className="ripple-loader" aria-label="Loading forecast">
          <span></span>
          <span></span>
          <span></span>
        </div>
      ) : (
        <p className="idle-hint">Pick a species above to see today's odds.</p>
      )}

      <footer className="footer">
        <p className="footerSubText">
          * This percentage is a rough indicator. Conditions such as local
          population, noise, vibration and your own skill are not taken into
          account *
        </p>
        <p className="footerText">Developed by Jack Sinclair</p>
      </footer>
      <Analytics />
    </div>
  );
}

export default App;
